import React, { useCallback } from 'react'
import { Alert, Button, Card, Divider, Flex, message, Space, Spin, Tooltip, Typography } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { afterLoginHandler } from 'app-redux/user/actions'
import { useGenerateOtp, useSingleLogin } from 'utils/queries'
import { mobileViewSelector } from 'app-redux/selectors'
import useNavigation from 'utils/hooks/useNavigation'
import { closeLoginForm } from 'app-redux/loginSlice'
import TwoFactorRequiredForm from './TwoFactorRequiredForm'

const { Title, Text } = Typography

const TwoFactorRequired = ({ email, isOnModal = false, isTemporary = false }) => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const { onNavigate } = useNavigation()
  const isMobileView = useSelector(mobileViewSelector)

  const generateOtpMutation = useGenerateOtp()
  const singleLoginMutation = useSingleLogin()

  const handleSubmit = useCallback(
    (values, callback) => {
      singleLoginMutation.mutate(
        { password: values.code, email, is_otp: 1, remember_me: Number(isTemporary) },
        {
          onSuccess: (response) => {
            if (response.data.success) {
              // Close agar ketika dapat error dari otp dan percobaan kedua sukses
              // tidak akan muncul modal error padahal login dengan otp required sukses
              dispatch(closeLoginForm())
              dispatch(afterLoginHandler(response, { email }))
              if (isOnModal) {
                window.location.reload(true)
              }
            } else {
              if (typeof callback === 'function') {
                callback()
              }
              message.error(response.data?.message || 'Failed to verify code')
            }
          },
          onError: (e) => {
            if (typeof callback === 'function') {
              callback()
            }
            message.error(e?.message || e?.data?.message)
          },
        },
      )
    },
    [dispatch, email, isOnModal, isTemporary, singleLoginMutation],
  )

  const handleResend = useCallback(
    ({ via, callback }) => {
      const payload = { email }
      if (via) {
        payload.via = via
      }

      generateOtpMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.data.success) {
            if (callback) {
              callback()
            }
            let channelMessage = ''
            let viaMessage = via
            if (via) {
              if (via === 'wa') {
                viaMessage = 'Whatsapp'
              }
              if (via !== 'email') {
                viaMessage = `${formatMessage({ id: 'report.number' })} ${viaMessage}`
              }
              channelMessage = formatMessage(
                { id: 'otp_has_been_resent_to_your_via_registered_channel' },
                { channel: viaMessage },
              )
            } else {
              channelMessage = formatMessage(
                { id: 'otp_has_been_resent_to_your_via_registered_channel' },
                { channel: 'email' },
              )
            }
            message.success(channelMessage)
          } else {
            message.error(response.data?.message || 'Failed to verify code')
          }
        },
        onError: (error) => {
          message.error(error?.message || 'Failed to verify code')
        },
      })
    },
    [generateOtpMutation, email, formatMessage],
  )

  const backToLogin = useCallback(() => {
    onNavigate('/user/login')
  }, [onNavigate])

  return (
    <Flex style={{ width: '100%', padding: 12, marginBottom: 80 }} justify="center" align="center">
      <Spin spinning={singleLoginMutation.isLoading}>
        <Card style={{ width: isMobileView ? '100%' : 450 }}>
          <Space direction="vertical" size={16}>
            <Flex align="center" justify="space-between">
              <Tooltip title="Kembali">
                <Button
                  shape="circle"
                  size="large"
                  icon={<ArrowLeftOutlined />}
                  onClick={backToLogin}
                />
              </Tooltip>
              <a target="_blank" rel="noopener noreferrer" href="https://kledo.com">
                <img
                  width={isMobileView ? 100 : 130}
                  src="resources/images/logo-color-kledo-front.png"
                  alt="Kledo"
                />
              </a>
            </Flex>
            <Divider style={{ marginTop: 0, marginBottom: 0 }} />
            <Alert
              message={formatMessage({
                id: 'application_detect_sigin_from_other_different_browser',
              })}
              type="info"
              showIcon
            />
            <Title style={{ marginBottom: 0, marginTop: 0 }} className="text-center" level={3}>
              {formatMessage({ id: 'verification_with_otp' })}
            </Title>
            <Text style={{ textAlign: 'center', display: 'block' }}>
              <FormattedMessage
                id="two_factor_verification_code_has_been_sent_to_your_email"
                values={{
                  email: <strong>{email}</strong>,
                }}
              />
            </Text>
          </Space>
          <TwoFactorRequiredForm
            onSubmit={handleSubmit}
            loading={singleLoginMutation.isLoading}
            onResend={!isTemporary ? handleResend : undefined}
            email={email}
            hideResend={isTemporary}
            resendLoading={generateOtpMutation.isLoading}
          />
        </Card>
      </Spin>
    </Flex>
  )
}

export default TwoFactorRequired
